import Seo, { pageSeo } from "../components/Seo";
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, PackageCheck, Truck, Warehouse } from "lucide-react";
import { PageBanner, Reveal, SectionTitle } from "../components/Shared";
import ShadeCatalogue from "../components/ShadeCatalogue";
import CatalogueButtons from "../components/CatalogueButtons";
import { products, productionSteps, productionHighlights, facilityPhotos, company } from "../mock";

const badgeIcon = { production: PackageCheck, delivery: Truck, stock: Warehouse };

const Production = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="production-page">
    <Seo {...pageSeo.production} />
      <PageBanner
        title="Production"
        subtitle="Our products, our process and the facility that brings colour to life."
        image={facilityPhotos.productionHouse.src}
        crumbs={["Production"]}
      />

      {/* Services list */}
      <section className="section bg-white" id="services">
        <div className="container-dic">
          <Reveal><SectionTitle sub="A complete range of inks, varnishes and coatings - formulated, colour-matched and manufactured at Medchal.">Products &amp; Services</SectionTitle></Reveal>
          <Reveal className="flex justify-center -mt-2 mb-12"><CatalogueButtons /></Reveal>
          <div className="space-y-10">
            {products.map((p, i) => (
              <Reveal key={p.id} className="scroll-mt-32" as="article">
                <div id={p.id} className={`grid md:grid-cols-5 gap-8 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`service-${p.id}`}>
                  <div className="md:col-span-2 overflow-hidden rounded-md shadow-md group">
                    <img src={p.image} alt={p.title} className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="md:col-span-3">
                    <span className="eyebrow">Service {String(i + 1).padStart(2, "0")}</span>
                    <h3 className="text-[24px] font-bold text-[#b5442a]">{p.title}</h3>
                    <p className="mt-3 text-[15px] leading-7 text-[#58595b] text-justify">{p.description}</p>
                    <Link to={{ pathname: "/contact", search: `?product=${p.id}`, hash: "#quote" }} className="inline-flex items-center gap-1 mt-4 text-[13px] font-bold uppercase tracking-wide text-[#7ab929] hover:text-[#5f9a17]" data-testid={`quote-link-${p.id}`}>Request a quote &rsaquo;</Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Shade card */}
      <ShadeCatalogue />

      {/* Process */}
      <section className="section applications-bg">
        <div className="container-dic relative z-10">
          <Reveal><SectionTitle>Our Manufacturing Process</SectionTitle></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productionSteps.map((s, i) => (
              <Reveal key={s.step} delay={i * 70} className="process-card">
                <span className="process-card__num">{s.step}</span>
                <h6 className="text-[17px] font-bold text-[#231f20] mt-2">{s.title}</h6>
                <p className="mt-2 text-[14px] leading-6 text-[#58595b]">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights: production house, delivery, stock */}
      {productionHighlights.map((h, idx) => {
        const Icon = badgeIcon[h.id];
        const reverse = idx % 2 === 1;
        return (
          <section key={h.id} id={h.id} className={`section scroll-mt-28 ${idx % 2 ? "bg-[#f7f7f7]" : "bg-white"}`} data-testid={`highlight-${h.id}`}>
            <div className={`container-dic grid lg:grid-cols-2 gap-12 items-center`}>
              <Reveal className={reverse ? "lg:order-2" : ""}>
                <span className="inline-flex items-center gap-2 bg-[#8cc63f]/15 text-[#5f9a17] text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  <Icon size={14} /> {h.badge}
                </span>
                <h2 className="section-title text-left mt-4">{h.title}</h2>
                <p className="mt-5 text-[15px] leading-7 text-[#58595b] text-justify">{h.text}</p>
                {h.id === "stock" && (
                  <ul className="mt-5 space-y-2 text-[14.5px] text-[#4d4d4f]">
                    {["Standard shades & bases always in stock", "Bulk orders for campaigns and seasonal runs", "Emergency top-ups dispatched within 24 hours", "Call " + company.phoneDisplay + " for immediate availability"].map((t) => (
                      <li key={t} className="flex items-start gap-2"><CheckCircle2 size={17} className="text-[#7ab929] mt-0.5 shrink-0" /> {t}</li>
                    ))}
                  </ul>
                )}
                <Link to={h.id === "stock" ? { pathname: "/contact", hash: "#quote" } : "/contact"} className="btn-green mt-7" data-testid={`highlight-cta-${h.id}`}>
                  {h.id === "stock" ? "Place a Bulk / Emergency Order" : "Reach Us"}
                </Link>
              </Reveal>
              <Reveal delay={120} className={`${reverse ? "lg:order-1" : ""}`}>
                {h.images.length === 3 && (
                  <div className="grid grid-cols-2 gap-4">
                    <img src={h.images[0].src} alt={h.images[0].alt} className="col-span-2 h-60 w-full object-cover rounded-md shadow-md" loading="lazy" />
                    <img src={h.images[1].src} alt={h.images[1].alt} className="h-44 w-full object-cover rounded-md shadow-md" loading="lazy" />
                    <img src={h.images[2].src} alt={h.images[2].alt} className="h-44 w-full object-cover rounded-md shadow-md" loading="lazy" />
                  </div>
                )}
                {h.images.length === 1 && (
                  <div className="relative rounded-md shadow-lg overflow-hidden bg-[#1f2a33]">
                    <img src={h.images[0].src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50" />
                    <img src={h.images[0].src} alt={h.images[0].alt} className="relative h-[440px] w-full object-contain" loading="lazy" />
                    <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur px-5 py-3 rounded-md shadow-md">
                      <div className="text-[12px] uppercase tracking-wider text-[#6d6e71]">Sample</div>
                      <div className="font-bold text-[#231f20]">Branded drum - packed &amp; ready for delivery</div>
                    </div>
                  </div>
                )}
                {h.images.length === 2 && (
                  <div className="grid grid-cols-2 gap-4">
                    <img src={h.images[0].src} alt={h.images[0].alt} className="h-72 w-full object-cover rounded-md shadow-md mt-8" loading="lazy" />
                    <img src={h.images[1].src} alt={h.images[1].alt} className="h-72 w-full object-cover rounded-md shadow-md" loading="lazy" />
                  </div>
                )}
              </Reveal>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default Production;
